import { useEffect, useState } from 'react'
import { useForm } from '@tanstack/react-form'
import { useUploadDepositReceipt } from '../hooks/useUploadDepositReceipt'
import { DepositReceiptUploadSchema } from '../schema/donationSchema'
import type { DepositReceiptUploadData } from '../schema/donationSchema'
import type { ApiError } from '@/types/api'
import { Modal } from '@/components/custom/modal'
import { Button } from '@/components/ui/button'
import { useIsMobile } from '@/hooks'
import { useDonationsStore } from '@/features/donations/store/donationsStore'
import { useMembersStore } from '@/features/members/store/membersStore'
import { formatCurrency } from '@/utils/currencyUtils'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { notifyRequestError } from '@/utils/notifyRequestError'
import DatePicker from '@/components/custom/date-picker'
import { FormTextarea } from '@/components/custom/form-textarea'
import { DragDropInput } from '@/components/custom/drag-drop-input'

interface UploadDepositReceiptFormProps {
  donationId: string
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
  onUploaded?: (uploadedId: string) => void
}

export function UploadDepositReceiptForm({
  donationId,
  trigger,
  open,
  onClose,
  onUploaded,
}: UploadDepositReceiptFormProps) {
  const isMobile = useIsMobile()
  const { mutateAsync: uploadDepositReceipt, isPending } =
    useUploadDepositReceipt()

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const donation = useDonationsStore((s) =>
    s.donations.find((d) => d._id === donationId),
  )
  const church = useChurchesStore((s) =>
    s.churches.find((c) => c._id === donation?.churchId),
  )
  const donor = useMembersStore((s) =>
    s.members.find((m) => m._id === donation?.donorId),
  )
  const donorName = donor?.name ?? 'Unknown donor'
  const amountDisplay = donation ? formatCurrency(donation.amount) : ''
  const churchName = church ? church.name : 'Unassigned Church'

  // Initialize form with TanStack Form
  const form = useForm({
    defaultValues: {
      donationId,
      depositDate: '',
      depositReceipt: null as File | null,
      notes: donation?.notes || '',
    } as DepositReceiptUploadData,
    onSubmit: async ({ value }) => {
      try {
        await uploadDepositReceipt({
          ...value,
          file: value.depositReceipt!,
        })

        handleDialogClose()
        setTimeout(() => {
          onUploaded?.(donationId)
        }, 0)
      } catch (error) {
        console.error('Upload failed:', error)
        notifyRequestError(
          error as ApiError,
          'Error occurred while submitting donation form',
        )
      }
    },
  })

  const handleDialogClose = () => {
    form.reset()
    setPreviewUrl(null)
    onClose?.()
  }

  const handleFileSelect = (file: File | null) => {
    form.setFieldValue('depositReceipt', file)
    if (file) {
      setPreviewUrl(URL.createObjectURL(file))
    } else {
      setPreviewUrl(null)
    }
  }

  useEffect(() => {
    if (!open) {
      const timeout = setTimeout(() => handleDialogClose(), 150)
      return () => clearTimeout(timeout)
    } else {
      form.reset()
      form.setFieldValue('donationId', donationId)
      form.setFieldValue('depositDate', '')
      setPreviewUrl(null)
    }
  }, [open, donationId])

  const formContent = (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="space-y-4"
    >
      <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
        <div className="flex items-center justify-between">
          <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>
            Church
          </div>
          <div className={`${isMobile ? 'text-sm' : 'font-semibold'}`}>
            {churchName}
          </div>
        </div>
        <div className="flex items-center justify-between mt-1">
          <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>
            Donor
          </div>
          <div
            className={`${isMobile ? 'text-sm' : 'font-medium'} text-gray-900`}
          >
            {donorName}
          </div>
        </div>
        <div className="flex items-center justify-between mt-1">
          <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>
            Amount
          </div>
          <div
            className={`${isMobile ? 'text-sm' : 'font-semibold'} text-green-600`}
          >
            {amountDisplay}
          </div>
        </div>
      </div>

      {/* File Upload Field */}
      <form.Field
        name="depositReceipt"
        validators={{
          onChange: ({ value }) => {
            const result =
              DepositReceiptUploadSchema.shape.depositReceipt.safeParse(value)
            return result.success ? undefined : result.error.errors[0].message
          },
        }}
      >
        {(field) => (
          <DragDropInput
            onFileSelect={handleFileSelect}
            selectedFile={field.state.value}
            accept="image/*,application/pdf"
            label="Deposit Receipt"
            required
            error={
              field.state.meta.errors.length > 0
                ? String(field.state.meta.errors[0])
                : undefined
            }
            previewUrl={previewUrl}
          />
        )}
      </form.Field>

      {/* Deposit Date Field */}
      <form.Field
        name="depositDate"
        children={(field) => (
          <div className="gap-0">
            <DatePicker
              field={field}
              label="Deposit Date"
              id={field.name}
              name={field.name}
              startYear={2000}
              disableFutureDates
              required
            />
          </div>
        )}
      />

      <form.Field
        name="notes"
        validators={{
          onBlur: ({ value }) => {
            const result =
              DepositReceiptUploadSchema.shape.notes.safeParse(value)
            return result.success ? undefined : result.error.errors[0].message
          },
        }}
        children={(field) => (
          <div className="gap-0">
            <FormTextarea
              field={field}
              label="Notes"
              id={field.name}
              name={field.name}
            />
          </div>
        )}
      />

      {/* Submit button */}
      <form.Subscribe
        selector={(state) => [state.canSubmit, state.isSubmitting]}
      >
        {([canSubmit, isSubmitting]) => (
          <Button
            type="submit"
            className="w-full mt-2"
            disabled={!canSubmit || isSubmitting || isPending}
          >
            {isSubmitting || isPending ? 'Uploading...' : 'Upload Receipt'}
          </Button>
        )}
      </form.Subscribe>

      {/* Form-level errors */}
      <form.Subscribe selector={(state) => state.errors}>
        {(errors) =>
          errors.length > 0 && (
            <div className="text-sm text-red-600">
              <p>Please fix the following errors:</p>
              <ul className="list-disc list-inside">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )
        }
      </form.Subscribe>
    </form>
  )

  return (
    <Modal
      open={open}
      onClose={handleDialogClose}
      trigger={trigger}
      title="Upload Deposit Receipt"
      contentClassName="max-w-md w-full"
    >
      {formContent}
    </Modal>
  )
}
